/** Weekly labour trend charts for the Labour Management demo screen - scheduled vs actual
 * hours, labour cost %, absence rate and overtime hours across the current week's mock
 * data (see `WEEKLY_TREND` in `data.ts`). Uses `recharts`, already a dependency used
 * elsewhere in the app (e.g. `TaskReportingDashboard`). */

import { Box, Grid, Typography, useTheme } from '@mui/material';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { WeeklyTrendPoint } from '../types';

interface WeeklyTrendProps {
  data: WeeklyTrendPoint[];
}

const CHART_HEIGHT = 200;

const WeeklyTrend = ({ data }: WeeklyTrendProps) => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const gray = theme.palette.custom.gray[400];

  return (
    <Grid container
      spacing={4}>
      <Grid item
        xs={12}
        md={6}>
        <Typography variant="textSM"
          fontWeight={600}
          sx={{ mb: 1 }}>
          {t('labourManagementScheduledVsActual', 'Scheduled hours vs actual hours')}
        </Typography>
        <Box sx={{ width: '100%', height: CHART_HEIGHT }}>
          <ResponsiveContainer>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <RechartsTooltip />
              <Bar dataKey="scheduledHours"
                name={t('labourManagementScheduledHours', 'Scheduled hours')}
                fill={gray} />
              <Bar dataKey="actualHours"
                name={t('labourManagementActualHours', 'Actual hours')}
                fill={primary} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <Typography variant="textSM"
          fontWeight={600}
          sx={{ mb: 1 }}>
          {t('labourManagementLabourCostPercentByDay', 'Labour cost % by day')}
        </Typography>
        <Box sx={{ width: '100%', height: CHART_HEIGHT }}>
          <ResponsiveContainer>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis domain={[25, 32]} />
              <RechartsTooltip />
              <Line type="monotone"
                dataKey="labourCostPercentage"
                name={t('labourManagementLabourCostPercent', 'Labour cost %')}
                stroke={theme.palette.warning.main}
                strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <Typography variant="textSM"
          fontWeight={600}
          sx={{ mb: 1 }}>
          {t('labourManagementAbsenceTrend', 'Absence trend')}
        </Typography>
        <Box sx={{ width: '100%', height: CHART_HEIGHT }}>
          <ResponsiveContainer>
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <RechartsTooltip />
              <Area type="monotone"
                dataKey="absenceRate"
                name={t('labourManagementAbsenceRate', 'Absence rate')}
                stroke={theme.palette.error.main}
                fill={theme.palette.error.light} />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <Typography variant="textSM"
          fontWeight={600}
          sx={{ mb: 1 }}>
          {t('labourManagementOvertimeTrend', 'Overtime trend')}
        </Typography>
        <Box sx={{ width: '100%', height: CHART_HEIGHT }}>
          <ResponsiveContainer>
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <RechartsTooltip />
              <Area type="monotone"
                dataKey="overtimeHours"
                name={t('labourManagementOvertimeHours', 'Overtime hours')}
                stroke={theme.palette.warning.main}
                fill={theme.palette.warning.light} />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Grid>
    </Grid>
  );
};

export default WeeklyTrend;
